import React, { useState } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { format } from "date-fns";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useCart, CartItem } from "@/contexts/CartContext";
import { User, Mail, Phone, MapPin, Car, Calendar as CalendarIcon, Tent } from "lucide-react";
import { CampingRegulationsDialog, CampingRegSection } from "./CampingRegulationsDialog";


// Define the props for the component
export interface GeneralCampingFormProps {
  onCancel?: () => void;
  editData?: CartItem | null;
}

const Required = () => <span className="text-red-500 ml-1">*</span>;

const formSchema = z
  .object({
    fullName: z.string().min(2, { message: "Full name must be at least 2 characters." }),
    email: z.string().email({ message: "Please enter a valid email address." }),
    phone: z.string().min(10, { message: "Please enter a valid phone number." }),
    address: z.string().min(5, { message: "Street address is required." }),
    city: z.string().min(2, { message: "City is required." }),
    state: z.string().min(2, { message: "State is required." }),
    zipCode: z.string().min(5, { message: "ZIP code is required." }),
    licensePlate: z.string().optional(),
    
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    campingRulesAck: z.boolean().refine((v) => v === true, {
      message: "You must acknowledge the camping rules.",
    }),
    fireWildlifeAck: z.boolean().refine((v) => v === true, {
      message: "You must acknowledge the fire & wildlife rules.",
    }),
    firearmsAck: z.boolean().refine((v) => v === true, {
      message: "You must acknowledge the firearms policy.",
    }),

  })
  .refine((data) => !!data.startDate && !!data.endDate, {
    message: "Select your camping dates.",
    path: ["startDate"],
  })
  .refine(
    (data) => !data.startDate || !data.endDate || data.endDate > data.startDate,
    {
      message: "End date must be after start date (minimum 1 night).",
      path: ["endDate"],
    }
  );

type BookingFormValues = z.infer<typeof formSchema>;

const GeneralCampingForm: React.FC<GeneralCampingFormProps> = ({ onCancel, editData }) => {
  const { toast } = useToast();
  const { addItem, updateItem } = useCart();
  const navigate = useNavigate();
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [regDialogOpen, setRegDialogOpen] = useState(false);
  const [regSection, setRegSection] = useState<CampingRegSection | undefined>(undefined);

  const openRegs = (section: CampingRegSection) => {
    setRegSection(section);
    setRegDialogOpen(true);
  };

  const isEditMode = !!editData;

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: editData?.customerName || "",
      email: editData?.customerEmail || "",
      phone: editData?.phoneNumber || "",
      address: editData?.address || "",
      city: editData?.city || "",
      state: editData?.state || "",
      zipCode: editData?.zipCode || "",
      licensePlate: editData?.licensePlate || "",
      startDate: editData?.startDate ? new Date(editData.startDate) : undefined,
      endDate: editData?.endDate ? new Date(editData.endDate) : undefined,
      campingRulesAck: editData?.legalAcknowledgment || false,
      fireWildlifeAck: editData?.legalAcknowledgment || false,
      firearmsAck: editData?.legalAcknowledgment || false,

    },
  });

  const { watch } = form;
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  // Live price preview (camping is exclusive-day counted = nights between dates)
  React.useEffect(() => {
    if (startDate && endDate) {
      const nights = Math.max(
        0,
        Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      );
      setCalculatedPrice(nights * 15);
    } else {
      setCalculatedPrice(0);
    }
  }, [startDate, endDate]);

  const handleSubmit = (values: BookingFormValues) => {
    if (!values.startDate || !values.endDate) {
      toast({
        title: "Invalid dates",
        description: "Please select valid start and end dates for your booking",
        variant: "destructive",
      });
      return;
    }

    const uniqueCartItemId = editData?.id || `general-camping-${Date.now()}`;
    const generalCampingSiteId = editData?.siteId || "general-camping-site";

    // NOTE (Step 1): cart-item shape intentionally unchanged. Step 2 will
    // add campingTotal/campingPrice/campingStartDate/campingEndDate/days.
    const itemData = {
      id: uniqueCartItemId,
      type: "general-camping" as const,
      permitCategory: "camping",
      permitName: `General Camping`,
      isAnnual: false,
      customerName: values.fullName,
      customerEmail: values.email,
      startDate: values.startDate,
      endDate: values.endDate,
      includesCamping: true,
      basePrice: 15,
      totalPrice: calculatedPrice,
      phoneNumber: values.phone,
      address: values.address,
      city: values.city,
      state: values.state,
      zipCode: values.zipCode,
      licensePlate: values.licensePlate || "",
      legalAcknowledgment: values.campingRulesAck && values.fireWildlifeAck && values.firearmsAck,
      siteId: generalCampingSiteId,
    };

    if (isEditMode && editData?.id) {
      updateItem(editData.id, itemData);
      toast({
        title: "Item updated!",
        description: "General camping permit has been updated in your cart.",
      });
    } else {
      addItem(itemData as CartItem);
      toast({
        title: "Added to cart!",
        description: "General camping permit added to cart.",
      });
    }

    if (onCancel) onCancel();
    navigate("/cart");
  };

  const nights =
    startDate && endDate
      ? Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-medium">
          {isEditMode ? "Edit General Camping Booking" : "General Camping Booking"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isEditMode
            ? "Update your general camping permit details"
            : "Book your general camping permit"}
        </p>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Customer Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-tribal-secondary">Customer Information</h3>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        Full Name <Required />
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <Mail className="w-4 h-4 mr-2" />
                        Email Address <Required />
                      </FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="you@example.com" {...field} />
                      </FormControl>
                      <p className="text-xs text-tribal-neutral mt-1">
                        Your permit will be sent to this email address
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      Phone Number <Required />
                    </FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="(555) 123-4567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Address Information */}
              <div>
                <FormLabel className="flex items-center mb-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  Address Information <Required />
                </FormLabel>
                <div className="grid gap-4">
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input placeholder="Enter street address" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid gap-4 md:grid-cols-3">
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            City <Required />
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter city" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            State <Required />
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter state" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="zipCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            ZIP Code <Required />
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter ZIP code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              <FormField
                control={form.control}
                name="licensePlate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      <Car className="w-4 h-4 mr-2" />
                      Vehicle License Plate
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="ABC123"
                        className="uppercase"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </FormControl>
                    <p className="text-xs text-tribal-neutral mt-1">
                      Optional: Helps with vehicle identification
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Camping Details */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-lg text-tribal-secondary flex items-center">
                <Tent className="w-5 h-5 mr-2" />
                Camping Details
              </h3>

              <div className="rounded-md border border-tribal-primary/20 bg-tribal-primary/5 p-3 text-sm text-tribal-secondary">
                <span className="font-medium">$15</span> per night
              </div>



              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Camping Dates <Required />
                    </FormLabel>
                    <FormControl>
                      <div className="rounded-md border border-input bg-background p-2">
                        <Calendar
                          mode="range"
                          fixedWeeks
                          numberOfMonths={1}
                          defaultMonth={field.value || new Date()}
                          selected={{
                            from: field.value || undefined,
                            to: form.getValues("endDate") || undefined,
                          }}
                          onSelect={(range) => {
                            if (!range?.from) {
                              field.onChange(undefined);
                              form.setValue("endDate", undefined as any, { shouldValidate: true });
                              return;
                            }
                            field.onChange(range.from);
                            form.setValue(
                              "endDate",
                              (range.to || range.from) as any,
                              { shouldValidate: true }
                            );
                          }}
                          disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </div>
                    </FormControl>
                    <p className="text-xs text-muted-foreground mt-1">
                      {field.value && form.getValues("endDate")
                        ? `${format(field.value, "MMM d, yyyy")} → ${format(
                            form.getValues("endDate")!,
                            "MMM d, yyyy"
                          )}`
                        : field.value
                        ? `${format(field.value, "MMM d, yyyy")} — pick an end date`
                        : "Click a start date, then an end date."}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />


              {/* Estimated total preview */}
              {calculatedPrice > 0 && (
                <div className="rounded-md border border-tribal-primary/20 bg-tribal-primary/5 p-3 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {nights} night{nights === 1 ? "" : "s"}
                  </span>
                  <span className="text-base font-semibold text-tribal-secondary">
                    Estimated total: ${calculatedPrice.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Acknowledgments */}
            <div className="space-y-3 pt-2">
              <FormField
                control={form.control}
                name="campingRulesAck"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">
                        I acknowledge and agree to the{" "}
                        <button type="button" onClick={() => openRegs("general-camping")} className="text-tribal-primary underline hover:opacity-80">camping regulations</button>,{" "}
                        <button type="button" onClick={() => openRegs("quiet-hours")} className="text-tribal-primary underline hover:opacity-80">quiet hours</button>{" "}
                        (10pm–7am), and{" "}
                        <button type="button" onClick={() => openRegs("trash-cleanliness")} className="text-tribal-primary underline hover:opacity-80">trash & cleanliness</button>{" "}
                        rules <Required />
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="fireWildlifeAck"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">
                        I understand the{" "}
                        <button type="button" onClick={() => openRegs("campfire-safety")} className="text-tribal-primary underline hover:opacity-80">campfire safety</button>{" "}
                        rules and{" "}
                        <button type="button" onClick={() => openRegs("wildlife-precautions")} className="text-tribal-primary underline hover:opacity-80">wildlife precautions</button>{" "}
                        (snakes, ticks, mosquitoes) on tribal lands <Required />
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="firearmsAck"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">
                        I acknowledge that{" "}
                        <button type="button" onClick={() => openRegs("firearms")} className="text-tribal-primary underline hover:opacity-80">discharging firearms</button>{" "}
                        is strictly prohibited in all camping areas <Required />
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-between pt-2">
              <Button type="submit">{isEditMode ? "Update Booking" : "Add to Cart"}</Button>
              <Button type="button" variant="outline" onClick={() => onCancel?.()}>
                Cancel
              </Button>
            </div>
          </form>
        </Form>

        <CampingRegulationsDialog
          open={regDialogOpen}
          onOpenChange={setRegDialogOpen}
          defaultSection={regSection}
        />

      </CardContent>
    </Card>
  );
};

export default GeneralCampingForm;
